/**
 * Impersonation Badge
 * A floating badge (bottom-left) shown when an admin is "viewing as" another user.
 * Deliberately NOT a full-width top bar — that overlaid the top chrome and blocked
 * the hamburger menu. Collapsible: a small eye dot when collapsed, the full pill
 * (who you're viewing as + Exit) when expanded. The choice is remembered.
 */

import { useState } from 'react';
import { X, Eye, ChevronLeft } from 'lucide-react';
import { useImpersonationStore } from '../../stores/impersonationStore';

const COLLAPSE_KEY = 'imp_badge_collapsed';

export function ImpersonationBanner() {
  const { isImpersonating, impersonatedUser, adminUser, stopImpersonation } = useImpersonationStore();
  const [expanded, setExpandedState] = useState<boolean>(() => {
    try { return localStorage.getItem(COLLAPSE_KEY) !== '1'; } catch { return true; }
  });

  const setExpanded = (v: boolean) => {
    setExpandedState(v);
    try { localStorage.setItem(COLLAPSE_KEY, v ? '0' : '1'); } catch { /* ignore */ }
  };

  if (!isImpersonating || !impersonatedUser || !adminUser) {
    return null;
  }

  const handleStopImpersonation = () => {
    stopImpersonation();
    // Reload the page to refresh all data with admin's context
    window.location.href = '/admin/users';
  };

  const who = impersonatedUser.displayName || impersonatedUser.email;

  // Collapsed: a small amber eye dot. Click to expand.
  if (!expanded) {
    return (
      <div className="fixed bottom-4 left-4 z-[9999]">
        <button
          onClick={() => setExpanded(true)}
          title={`Viewing as ${who} — click to expand`}
          aria-label="Expand impersonation badge"
          className="flex h-9 w-9 items-center justify-center rounded-full bg-amber-500 text-amber-950 shadow-lg ring-1 ring-amber-600/40 transition-colors hover:bg-amber-400"
        >
          <Eye className="h-4 w-4" />
        </button>
      </div>
    );
  }

  // Expanded: full pill with controls + a collapse chevron.
  return (
    <div className="fixed bottom-4 left-4 z-[9999]">
      <div
        className="flex items-center gap-2 rounded-full bg-amber-500 text-amber-950 py-1.5 pl-3 pr-1.5 shadow-lg ring-1 ring-amber-600/40"
        title={`admin: ${adminUser.email}`}
      >
        <Eye className="h-3.5 w-3.5 shrink-0" />
        <span className="max-w-[180px] truncate text-xs font-medium">
          Viewing as <strong className="font-semibold">{who}</strong>
        </span>
        <button
          onClick={handleStopImpersonation}
          className="ml-1 flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-900 transition-colors hover:bg-amber-200"
        >
          <X className="h-3 w-3" />
          Exit
        </button>
        <button
          onClick={() => setExpanded(false)}
          title="Collapse"
          aria-label="Collapse impersonation badge"
          className="flex h-5 w-5 items-center justify-center rounded-full transition-colors hover:bg-amber-600/20"
        >
          <ChevronLeft className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
